import { logError } from '@ptek/client-logger';
import { useWidgetContext } from '@ptek/widget-core';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { reportMalformedHostPayload } from '../config/reportMalformedHostPayload';
import { AppContext } from '../context/AppContext';
import { useRecordingStore } from '../stores';
import { AmbientScribeContext, Appointment, ChartNoteFont } from '../types';
import { useSessionIdResolution } from './useSessionIdResolution';

export interface EncounterData {
  appointment: {
    id: string;
    patientName: string;
    patient?: {
      firstName?: string;
      lastName?: string;
      dateOfBirth?: string;
      gender?: string;
    };
    title?: string;
    sessionId?: string;
    serviceDate?: string;
    chartNoteFont?: ChartNoteFont;
  };
}

export const useEncounterHandler = () => {
  const { updateContext, widgetContext } =
    useWidgetContext<AmbientScribeContext>();

  const [pendingPatientSwitch, setPendingPatientSwitch] = useState<{
    newRecordingSession: Appointment;
  } | null>(null);

  const { sessionModel } = useContext(AppContext);
  const stopInFlightRef = useRef(false);

  const {
    isRecording: isCurrentlyRecording,
    isPaused,
    unifiedSessionIdEnabled,
    setActiveRecordingSession,
    clearActiveRecording,
    activeRecordingSession,
    callStopRecording,
    callAbortRecording,
    setPendingEncounter,
    setPendingContextData,
  } = useRecordingStore();

  // The hook runs the adoption effect (host-supplied sessionId on reopen).
  useSessionIdResolution();

  useEffect(() => {
    if (pendingPatientSwitch) {
      const isProcessingComplete =
        !isCurrentlyRecording && !sessionModel.isGenerating;

      if (isProcessingComplete) {
        updateContext({
          ...widgetContext.context,
          appointment: pendingPatientSwitch.newRecordingSession,
          sessionId: pendingPatientSwitch.newRecordingSession.sessionId,
        });

        clearActiveRecording();
        setActiveRecordingSession(pendingPatientSwitch.newRecordingSession);
        setPendingPatientSwitch(null);
      }
    }
  }, [
    pendingPatientSwitch,
    isCurrentlyRecording,
    sessionModel.isGenerating,
    clearActiveRecording,
    setActiveRecordingSession,
    updateContext,
    widgetContext.context,
  ]);

  const handleSetEncounter = useCallback(
    async (data: EncounterData) => {
      const appointment = data?.appointment;

      if (!appointment || !appointment.id) {
        reportMalformedHostPayload(
          'useEncounterHandler',
          'Ignoring malformed setEncounter payload (missing appointment.id):',
          data
        );
        return;
      }

      const isAppointmentChanged =
        activeRecordingSession?.id !== appointment.id;
      const isContextEncounterChanged =
        widgetContext.context.appointment?.id !== appointment.id;
      // Preserve an asynchronously supplied host session ID across remounts without letting an id-less event erase an adopted session.
      const isSessionIdChanged =
        Boolean(appointment.sessionId) &&
        (activeRecordingSession?.sessionId !== appointment.sessionId ||
          widgetContext.context.appointment?.sessionId !==
            appointment.sessionId);

      // The host came back to the open patient: drop a switch parked away from
      // it so the widget never moves while the host shows this patient.
      const parkedAppointmentId =
        useRecordingStore.getState().pendingEncounter?.id ??
        pendingPatientSwitch?.newRecordingSession.id;
      if (
        !isAppointmentChanged &&
        parkedAppointmentId &&
        parkedAppointmentId !== appointment.id
      ) {
        setPendingPatientSwitch(null);
        setPendingEncounter(null);
        setPendingContextData(null);
      }

      if (
        !isAppointmentChanged &&
        !isContextEncounterChanged &&
        !isSessionIdChanged
      ) {
        return;
      }

      if (pendingPatientSwitch?.newRecordingSession?.id === appointment.id) {
        return;
      }

      const newRecordingSession: Appointment = {
        ...appointment,
        id: appointment.id,
        patientName: appointment.patientName,
        patient: appointment.patient,
        time: new Date().toISOString(),
        duration: '0:00',
        title: appointment.title,
        encounterId: appointment.id,
        sessionId: appointment.sessionId,
        serviceDate: appointment.serviceDate,
      };

      if (!isAppointmentChanged) {
        if (!pendingPatientSwitch && !sessionModel.isGenerating) {
          updateContext({
            ...widgetContext.context,
            appointment: newRecordingSession,
          });
          setActiveRecordingSession(newRecordingSession);
        }
        return;
      }

      if (isCurrentlyRecording || stopInFlightRef.current) {
        // Set before the stop so context the host sends during it is held for this encounter.
        setPendingEncounter(newRecordingSession);
        // A stop for the open patient is already running; it parks the latest pending encounter.
        if (stopInFlightRef.current) return;
        stopInFlightRef.current = true;
        // Park the latest pending encounter; none if the host returned to the open patient during the stop.
        const parkPendingEncounter = () => {
          const latest = useRecordingStore.getState().pendingEncounter;
          if (latest) setPendingPatientSwitch({ newRecordingSession: latest });
        };
        try {
          if (isPaused && unifiedSessionIdEnabled) {
            await callAbortRecording();
            parkPendingEncounter();
          } else {
            await callStopRecording(parkPendingEncounter);
          }
        } catch (error) {
          logError(
            'useEncounterHandler',
            'Failed to initiate patient switch:',
            error
          );
          parkPendingEncounter();
        } finally {
          stopInFlightRef.current = false;
        }
      } else if (pendingPatientSwitch || sessionModel.isGenerating) {
        // Hold the switch until the open patient's note lands; the effect applies it.
        setPendingEncounter(newRecordingSession);
        setPendingPatientSwitch({ newRecordingSession });
      } else {
        updateContext({
          ...widgetContext.context,
          appointment: newRecordingSession,
        });
        clearActiveRecording();
        setActiveRecordingSession(newRecordingSession);
      }
    },
    [
      updateContext,
      widgetContext.context,
      clearActiveRecording,
      isCurrentlyRecording,
      isPaused,
      unifiedSessionIdEnabled,
      callAbortRecording,
      activeRecordingSession,
      setActiveRecordingSession,
      setPendingEncounter,
      setPendingContextData,
      pendingPatientSwitch,
      sessionModel.isGenerating,
    ]
  );

  return {
    handleSetEncounter,
  };
};
